import { Builder, withChildren, BuilderElement } from '@builder.io/react';
import image from '@/assets/images/cms/carousel.svg?inline';
import type { CarouselGapSize } from '@/components/shared/Carousel';

export interface CarouselComponentProps {
	readonly slides: readonly SlideProps[];
	readonly builderBlock: BuilderElement;
	readonly showArrows: boolean;
	readonly showArrowsOnMobile: boolean;
	readonly showDots: boolean;
	readonly gap?: CarouselGapSize;
	readonly autoPlay?: boolean;
	readonly autoPlaySpeed?: number;
	readonly customSlidesWidth?: string;
}

export interface SlideProps {
	readonly content: BuilderElement[];
}

const register = <TProps extends CarouselComponentProps>(Component: React.FC<TProps>): void => {
	const CarouselWithChildren = withChildren(Component);

	const defaultElement: BuilderElement = {
		'@type': '@builder.io/sdk:Element',
		children: [
			{
				'@type': '@builder.io/sdk:Element',
				responsiveStyles: {
					large: {
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'center',
						minHeight: '200px',
						backgroundColor: '#d9f1f1',
						textAlign: 'center',
					},
				},
				component: {
					name: 'Text',
					options: {
						text: 'I am a slide',
					},
				},
			},
		],
	};

	Builder.registerComponent(CarouselWithChildren, {
		name: 'Carousel',
		image,
		canHaveChildren: true,

		inputs: [
			{
				name: 'slides',
				type: 'list',
				broadcast: true,
				subFields: [
					{
						name: 'content',
						type: 'uiBlocks',
						hideFromUI: true,
						defaultValue: [defaultElement],
					},
				],
				defaultValue: [
					{
						content: [defaultElement],
					},
					{
						content: [defaultElement],
					},
				],
			},
			{
				name: 'showDots',
				helperText: 'Show pagination dots',
				type: 'boolean',
				defaultValue: true,
				broadcast: true,
			},
			{
				name: 'showArrows',
				helperText: 'Show navigation arrows',
				type: 'boolean',
				defaultValue: true,
				broadcast: true,
			},
			{
				name: 'autoPlay',
				helperText: 'Automatically rotate to the next slide every few seconds',
				type: 'boolean',
				defaultValue: true,
				broadcast: true,
			},
			{
				name: 'gap',
				helperText: 'Space between the slides',
				type: 'string',
				enum: ['sm', 'md', 'none'],
				defaultValue: 'md',
				advanced: true,
				broadcast: true,
			},
			{
				name: 'customSlidesWidth',
				helperText: `Custom width for the slides in percentage. E.g. "50" will make each slide 1/2 its container width.`,
				type: 'number',
				min: 0,
				max: 100,
				defaultValue: 100,
				advanced: true,
				broadcast: true,
			},
			{
				name: 'autoPlaySpeed',
				helperText:
					'Milliseconds to wait before automatically changing each slide. Only applies if autoplay is on',
				type: 'number',
				defaultValue: 7000,
				advanced: true,
				broadcast: true,
			},
			{
				name: 'showArrowsOnMobile',
				helperText: 'Show navigation arrows on mobile',
				type: 'boolean',
				defaultValue: false,
				advanced: true,
				broadcast: true,
			},
		],
	});
};

export default register;
